'use client'

import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  LetterCaseToggleIcon,
} from '@radix-ui/react-icons'
import * as React from 'react'
import { SearchAndReplaceStorage } from '~/lib/tiptap/extensions/rich-text/extensions/find-and-replace'
import { clsx } from 'clsx'
import { Input } from '~/sotano-kit/primitives/input'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { Editor, EditorEvents, Range } from '@tiptap/core'
import { useThrottledCallback } from '~/hooks/use-debounced-callback'
import { isHotkey } from 'is-hotkey'
import { useRichTextContext } from './context'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover'

export function FindAndReplaceToolbar({ editor }: { editor: Editor }) {
  const {
    searchText,
    setSearchText,
    replaceText,
    setReplaceText,
    findAndReplaceState,
    setFindAndReplaceState,
    searchInputRef,
  } = useRichTextContext()
  const editorRef = React.useRef(editor)
  // eslint-disable-next-line react-compiler/react-compiler
  editorRef.current = editor

  const replacing = findAndReplaceState === 'replacing'
  const [checked, setChecked] = React.useState(false)

  // functions
  const replace = React.useCallback(() => {
    editor?.chain().replace().run()
  }, [editor])
  const selectNext = React.useCallback(
    () => editor?.chain().selectNextResult().run(),
    [editor]
  )
  const replaceAll = React.useCallback(() => {
    editor?.chain().replaceAll().run()
    selectNext()
  }, [editor, selectNext])
  const selectPrevious = React.useCallback(
    () => editor?.chain().selectPreviousResult().run(),
    [editor]
  )

  const [results, setResults] = React.useState<
    SearchAndReplaceStorage['results']
  >(editor?.storage?.searchAndReplace.results)
  const [selectedResult, setSelectedResult] = React.useState<
    SearchAndReplaceStorage['selectedResult']
  >(editor?.storage?.searchAndReplace.selectedResult)

  // Force re-render when editor storage changes
  React.useEffect(() => {
    if (!editor) return

    const updateHandler = ({ editor }: EditorEvents['update']) => {
      setResults(editor.storage.searchAndReplace.results)
      setSelectedResult(editor.storage.searchAndReplace.selectedResult)
    }

    editor.on('transaction', updateHandler)

    return () => {
      editor.off('transaction', updateHandler)
    }
  }, [editor])

  // effect to set idle when pressing escape
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        e.preventDefault()
        const editor = editorRef.current
        const selectedRange =
          (editor.storage.searchAndReplace.results[
            editor.storage.searchAndReplace.selectedResult
          ] as Range) || undefined
        if (selectedRange) {
          editor.commands.setTextSelection(selectedRange.to)
        }
        setFindAndReplaceState('idle')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [setFindAndReplaceState])
  // debounce the navigation to the first result

  // Debounce the search term
  const setSearchTermDebounced = useThrottledCallback(
    (searchText: string) => {
      if (!searchText) return

      setTimeout(() => {
        editorRef.current?.chain().setSearchTerm(searchText).run()
        editorRef.current?.chain().selectFirstResult().run()
      })
    },
    [],
    500
  )

  React.useEffect(() => {
    setSearchTermDebounced(searchText)
  }, [searchText, setSearchTermDebounced])

  // Secondaries effects
  React.useEffect(() => {
    setTimeout(() => {
      editorRef.current?.chain().setReplaceTerm(replaceText).run()
    }, 100)
  }, [replaceText])

  React.useEffect(() => {
    setTimeout(() => {
      editorRef.current?.chain().setCaseSensitive(checked).run()
      editorRef.current?.chain().selectNextResult().run()
    }, 100)
  }, [checked])

  React.useEffect(() => {
    if (findAndReplaceState === 'idle') {
      setTimeout(() => {
        editorRef.current
          ?.chain()
          .setSearchTerm('')
          .setReplaceTerm('')
          .setCaseSensitive(false)
          .run()
        setChecked(false)
        setSearchText('')
        setReplaceText('')
      }, 100)
    }
  }, [findAndReplaceState, setReplaceText, setSearchText])

  // Focus on the search input when the search chanege to true
  React.useEffect(() => {
    // focus on the search input when the search chanege to true
    if (findAndReplaceState === 'searching') {
      searchInputRef.current?.focus({ preventScroll: true })
    } else {
      searchInputRef.current?.blur()
    }
  }, [findAndReplaceState, searchInputRef])

  return (
    <Popover open={findAndReplaceState !== 'idle'} modal={false}>
      <PopoverTrigger asChild>
        <div className="sticky left-0 pointer-events-none top-[calc(var(--navbar-element-height)+0px)] pr-5 right-0 w-px h-0 ml-auto" />
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => {
          e.preventDefault()
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
        side="bottom"
        align="end"
        alignOffset={5}
        className={clsx(
          '!z-tooltip',
          findAndReplaceState === 'idle' && 'invisible pointer-events-none'
        )}
      >
        <div
          className={clsx(
            'relative flex flex-col max-w-[400px] bg-dark-base rounded-lg border-dark-border-solid border text-xs text-dark-faint'
          )}
        >
          <div className="px-2">
            <div className="flex items-center gap-1.5 py-1.5">
              <Input
                ref={searchInputRef}
                value={searchText}
                className="w-48 flex-grow-0"
                onKeyDown={(e) => {
                  if (isHotkey('mod+f', e)) {
                    e.preventDefault()
                    setFindAndReplaceState('idle')
                  } else if (e.key === 'Enter') {
                    selectNext()
                    editor.state.applyTransaction(
                      editor.state.tr.setSelection(editor.state.selection)
                    )
                  }
                }}
                onChange={(e) => {
                  setSearchText(e.target.value)
                }}
                placeholder="Search..."
              />
              <IconButton
                className="size-9 self-stretch"
                onClick={() =>
                  setFindAndReplaceState(
                    findAndReplaceState === 'searching'
                      ? 'replacing'
                      : 'searching'
                  )
                }
              >
                <ChevronDownIcon
                  className={clsx('size-4', replacing && 'rotate-180')}
                />
              </IconButton>
              <IconButton
                className={clsx(
                  'size-9 self-stretch enabled:hover:!bg-dark-shade-hover',
                  checked &&
                    '!bg-dark-shade text-light-base border-dark-border-solid border'
                )}
                onClick={() => {
                  setChecked((prev) => !prev)
                }}
              >
                <LetterCaseToggleIcon className="size-4" />
              </IconButton>
            </div>
            {replacing && (
              <div className="flex gap-1.5 border-t border-dark-border-solid py-1.5">
                <Input
                  value={replaceText}
                  className="w-48 flex-grow-0"
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Replace..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      replace()
                      setTimeout(() => {
                        selectNext()
                      }, 100)
                    }
                  }}
                />
                <IconButton onClick={replace} className="size-9 self-stretch">
                  <ReplaceIcon className="size-4" />
                </IconButton>
                <IconButton
                  onClick={replaceAll}
                  className="size-9 self-stretch"
                >
                  <ReplaceAllIcon className="size-4" />
                </IconButton>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 border-t border-dark-border-solid py-1.5 p-2">
            <span className="min-w-[5ch] ml-2">
              {results?.length === 0 ? 0 : selectedResult + 1} of{' '}
              {results?.length}
            </span>
            <IconButton onClick={selectPrevious} className="size-7">
              <ArrowLeftIcon className="size-4" />
            </IconButton>
            <IconButton onClick={selectNext} className="size-7">
              <ArrowRightIcon className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

const ReplaceIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M14 4a2 2 0 0 1 2-2" />
    <path d="M16 10a2 2 0 0 1-2-2" />
    <path d="M20 2a2 2 0 0 1 2 2" />
    <path d="M22 8a2 2 0 0 1-2 2" />
    <path d="m3 7 3 3 3-3" />
    <path d="M6 10V5a3 3 0 0 1 3-3h1" />
    <rect x="2" y="14" width="8" height="8" rx="2" />
  </svg>
)

const ReplaceAllIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M14 14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2" />
    <path d="M14 4a2 2 0 0 1 2-2" />
    <path d="M16 10a2 2 0 0 1-2-2" />
    <path d="M20 14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2" />
    <path d="M20 2a2 2 0 0 1 2 2" />
    <path d="M22 8a2 2 0 0 1-2 2" />
    <path d="m3 7 3 3 3-3" />
    <path d="M6 10V5a 3 3 0 0 1 3-3h1" />
    <rect x="2" y="14" width="8" height="8" rx="2" />
  </svg>
)
